import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useLang } from '../../utils/i18n';

// Builds the link target for a card based on its linkType. Returns null
// when there's nothing to link to (linkType "none", or a missing target).
const getLinkHref = (promo) => {
    if (promo.linkType === 'product' && promo.linkProduct) {
        return `/product/${promo.linkProduct._id || promo.linkProduct}`;
    }
    if (promo.linkType === 'category' && promo.categorySlug) {
        return `/products?category=${promo.categorySlug}`;
    }
    if (promo.linkType === 'brand' && promo.linkBrand) {
        return `/products?brand=${encodeURIComponent(promo.linkBrand)}`;
    }
    return null;
};

const TrustSection = () => {
    const { lang } = useLang();
    const [trustPromos, setTrustPromos] = useState([]);

    // Fallback text shown until the real settings load (or if the request
    // fails), so the trust card is never blank.
    const [trustTitle, setTrustTitle] = useState("Why Shop With 1313 Mart");
    const [trustTitleZh, setTrustTitleZh] = useState("");
    const [trustBody, setTrustBody] = useState("Fresh groceries, honest pricing, and fast delivery — everything your family needs, sourced with care.");
    const [trustBodyZh, setTrustBodyZh] = useState("");

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const { data } = await axios.get('/api/v1/site-settings');
                if (data.settings.trustTitle) setTrustTitle(data.settings.trustTitle);
                if (data.settings.trustTitleZh) setTrustTitleZh(data.settings.trustTitleZh);
                if (data.settings.trustBody) setTrustBody(data.settings.trustBody);
                if (data.settings.trustBodyZh) setTrustBodyZh(data.settings.trustBodyZh);
            } catch (error) {
                console.error('Failed to load trust section settings', error);
            }
        };
        fetchSettings();

        const fetchTrustPromos = async () => {
            try {
                const { data } = await axios.get('/api/v1/trust-promos');
                setTrustPromos(data.promos);
            } catch (error) {
                console.error('Failed to load trust promos', error);
            }
        };
        fetchTrustPromos();
    }, []);

    if (trustPromos.length === 0) {
        // No admin-created cards yet — just show the trust card alone
        // rather than fabricating placeholder promo content.
        return (
            <div className="w-full lg:w-72 bg-yellow-50 rounded-lg p-6 flex flex-col gap-3">
                <VerifiedUserIcon className="text-primary-green" sx={{ fontSize: "28px" }} />
                <h3 className="text-lg font-semibold text-gray-800">{lang === 'zh' && trustTitleZh ? trustTitleZh : trustTitle}</h3>
                <div className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: lang === 'zh' && trustBodyZh ? trustBodyZh : trustBody }} />
            </div>
        );
    }

    return (
        <div className="flex flex-col lg:flex-row gap-6">

            {/* Trust card — intentionally no fabricated customer name/photo/quote,
                since we don't have real testimonials or reviews to draw from yet */}
            <div className="w-full lg:w-72 flex-shrink-0 bg-yellow-50 rounded-lg p-6 flex flex-col gap-3">
                <VerifiedUserIcon className="text-primary-green" sx={{ fontSize: "28px" }} />
                <h3 className="text-lg font-semibold text-gray-800">{lang === 'zh' && trustTitleZh ? trustTitleZh : trustTitle}</h3>
                <div className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: lang === 'zh' && trustBodyZh ? trustBodyZh : trustBody }} />
            </div>

            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-6">
                {trustPromos.map((promo) => {
                    const useZh = lang === 'zh';
                    const displayImage = (useZh && promo.imageZh?.url) ? promo.imageZh : promo.image;
                    const displayLabel = (useZh && promo.labelZh) ? promo.labelZh : promo.label;
                    const displayHeading = (useZh && promo.headingZh) ? promo.headingZh : promo.heading;
                    const displayDescription = (useZh && promo.descriptionZh) ? promo.descriptionZh : promo.description;
                    const displayButtonText = (useZh && promo.buttonTextZh) ? promo.buttonTextZh : promo.buttonText;
                    const hasImage = Boolean(displayImage?.url);
                    const href = getLinkHref(promo);
                    return (
                        <div key={promo._id} className={`relative overflow-hidden rounded-lg p-6 flex flex-col justify-between min-h-[180px] ${hasImage ? '' : 'bg-gray-100'}`}>
                            {hasImage && (
                                <img draggable="false" src={displayImage.url} alt={displayHeading || displayLabel} className="absolute inset-0 w-full h-full object-cover" />
                            )}
                            {hasImage && promo.showOverlay !== false && <div className="absolute inset-0 bg-black/30" />}
                            <div className="relative">
                                {displayLabel && (
                                    <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${hasImage ? 'text-white' : 'text-primary-green'}`}>{displayLabel}</p>
                                )}
                                {displayHeading && (
                                    <h3 className={`text-xl font-semibold mb-1 ${hasImage ? 'text-white' : 'text-gray-800'}`}>{displayHeading}</h3>
                                )}
                                {displayDescription && (
                                    <div className={`text-sm ${hasImage ? 'text-white opacity-90' : 'text-gray-500'}`} dangerouslySetInnerHTML={{ __html: displayDescription }} />
                                )}
                            </div>
                            {promo.showButton !== false && href && (
                                <Link to={href} className="relative mt-4 self-start bg-white border text-sm font-medium px-4 py-2 rounded-full text-gray-700 hover:border-primary-blue hover:text-primary-blue">
                                    {displayButtonText || "Shop Now"}
                                </Link>
                            )}
                        </div>
                    );
                })}
            </div>

        </div>
    );
};

export default TrustSection;